/**
 * Form Navigation Utility
 *
 * Handles proper page navigation for donation forms after reset/checkout.
 * Ensures correct behavior based on hosted page mode and embed configuration.
 */

/**
 * Navigate to the correct page after form reset/checkout
 *
 * Rules:
 * 1. NEVER navigate in hosted page mode (hosted pages stay on FORM)
 * 2. NEVER navigate in overlay mode (overlay always shows FORM — DETAIL_PAGE div is not rendered)
 * 2b. NEVER navigate in drawer mode (EmbedForm renders no DETAIL_PAGE in a drawer either, so
 *     sending the form there after a reset left the drawer blank: A11-003, found on the widget
 *     overlay's mobile drawer after a checkout round-trip)
 * 3. When enableEmbedForm === true: navigate to DETAIL_PAGE (detail page)
 * 4. When enableEmbedForm === false: navigate to FORM (skip detail page)
 *
 * @param {Object} params - Navigation parameters
 * @param {Function} params.setParentPage - Parent page setter (from EmbedForm/EventForm)
 * @param {boolean} params.isHostedPage - Whether form is in hosted page mode
 * @param {boolean} params.enableEmbedForm - Whether detail page is enabled
 * @param {string} params.formDisplayMode - Form display mode ('overlay' | 'dynamic')
 * @param {boolean} [params.isDrawerOpen] - Whether the form is rendered inside a drawer
 *
 * @example
 * // In form's resetFormState function:
 * navigateAfterReset({ setParentPage, isHostedPage, enableEmbedForm, formDisplayMode });
 */
export const navigateAfterReset = ({
  setParentPage,
  isHostedPage,
  enableEmbedForm,
  formDisplayMode,
  isDrawerOpen = false,
}) => {
  // CRITICAL: Never navigate in hosted page mode
  // Hosted pages always stay on FORM page - detail page content is shown in LeftPanel
  if (isHostedPage) {
    return;
  }

  // CRITICAL: Never navigate in overlay mode
  // Overlay always stays on FORM page — DETAIL_PAGE div is not rendered in overlay
  if (formDisplayMode === 'overlay') {
    return;
  }

  // CRITICAL: Never navigate in drawer mode — EmbedForm renders no DETAIL_PAGE
  // in a drawer, so the form would be replaced by nothing.
  if (isDrawerOpen) {
    return;
  }

  // Only navigate if we have a parent page setter
  if (!setParentPage || typeof setParentPage !== 'function') {
    return;
  }

  // Navigate based on enableEmbedForm configuration
  if (enableEmbedForm) {
    setParentPage('DETAIL_PAGE');  // Show detail page
  } else {
    setParentPage('FORM');          // Skip detail page
  }
};
